import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Header from '../layout/Header';
import LeadersSection from '../components/LeadersSection';
import CommunicationPRIsMEPrimesModal from '../components/CommunicationPRIsMEPrimesModal';
import { communicationPRIsMEPrimes, participantsComitePRIsME } from '../data/comitePRIsMECommunicationData';
import { carteEbaucheCadreBZ } from '../data/comitePRIsMECardsData';
import EbaucheCadreBZModal from '../components/EbaucheCadreBZModal';
import { Presentation, Users, Target, Clock, FileText, ChevronDown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLexLastSync } from '../contexts/LexLastSyncContext';
import ProcedureClonePRIsMEModal from '../components/ProcedureClonePRIsMEModal';
import ProcedureCloneCatalogueActifsModal from '../components/ProcedureCloneCatalogueActifsModal';
import GouvernancePRIsMEModal from '../components/GouvernancePRIsMEModal';
import GouvernancePRIsME20Modal from '../components/GouvernancePRIsME20Modal';
import ComiteTIPrismeModal from '../components/ComiteTIPrismeModal';
import ComiteTIClientsDashboardModal from '../components/ComiteTIClientsDashboardModal';
import { getComiteDashboard, type ComiteDashboardTotals } from '../services/comiteService';
import PrismeUserCreationGuideModal from '../components/PrismeUserCreationGuideModal';
import AikupCard from '../components/AikupCard';
import type { AikupItem } from '../data/aikupData';

/** Phases de la charte — fond toujours sombre + accent en bordure (lisible sur tout écran) */
const CHARTE_TEMPS_PHASES: Array<{
  phase: string;
  label: string;
  hours: string;
  title: string;
  bullets: string[];
  card: string;
  panel: string;
}> = [
  {
    phase: 'Phase 1',
    label: 'Prise de besoin & analyse',
    hours: '28 h',
    title: 'PHASE 1 – Prise de besoin et analyse stratégique (28 h)',
    bullets: [
      'Rencontre initiale',
      "Analyse de l'environnement TI",
      'Création du Prisme Client (20 h)',
      'Validation avec le client',
    ],
    card:
      'rounded-xl border-2 border-indigo-400/80 border-l-[6px] border-l-indigo-400 bg-slate-950/92 p-2.5 shadow-lg ring-1 ring-indigo-500/25 backdrop-blur-sm',
    panel:
      'rounded-2xl border-2 border-indigo-400/75 border-l-[8px] border-l-indigo-400 bg-slate-950/96 p-4 shadow-inner ring-1 ring-indigo-500/30',
  },
  {
    phase: 'Phase 2',
    label: 'Stratégie TI',
    hours: '15 h',
    title: 'PHASE 2 – Stratégie TI (15 h)',
    bullets: ['Définition des objectifs', 'Analyse des risques', 'Plan directeur TI', 'Présentation stratégique'],
    card:
      'rounded-xl border-2 border-violet-400/80 border-l-[6px] border-l-violet-400 bg-slate-950/92 p-2.5 shadow-lg ring-1 ring-violet-500/25 backdrop-blur-sm',
    panel:
      'rounded-2xl border-2 border-violet-400/75 border-l-[8px] border-l-violet-400 bg-slate-950/96 p-4 shadow-inner ring-1 ring-violet-500/30',
  },
  {
    phase: 'Phase 3',
    label: 'Conception & structuration',
    hours: '15 h',
    title: 'PHASE 3 – Conception et structuration (15 h)',
    bullets: ['Architecture cible', 'Gouvernance TI', 'Sécurité et conformité', 'Documentation'],
    card:
      'rounded-xl border-2 border-cyan-400/80 border-l-[6px] border-l-cyan-400 bg-slate-950/92 p-2.5 shadow-lg ring-1 ring-cyan-500/25 backdrop-blur-sm',
    panel:
      'rounded-2xl border-2 border-cyan-400/75 border-l-[8px] border-l-cyan-400 bg-slate-950/96 p-4 shadow-inner ring-1 ring-cyan-500/30',
  },
  {
    phase: 'Phase 4',
    label: 'Mise en œuvre',
    hours: 'Variable',
    title: 'PHASE 4 – Mise en œuvre (variable)',
    bullets: ['Déploiement technique', 'Tests et ajustements', 'Documentation finale'],
    card:
      'rounded-xl border-2 border-amber-400/85 border-l-[6px] border-l-amber-400 bg-slate-950/92 p-2.5 shadow-lg ring-1 ring-amber-500/25 backdrop-blur-sm',
    panel:
      'rounded-2xl border-2 border-amber-400/80 border-l-[8px] border-l-amber-400 bg-slate-950/96 p-4 shadow-inner ring-1 ring-amber-500/30',
  },
  {
    phase: 'Phase 5',
    label: 'Gouvernance continue',
    hours: '3 à 5 h / mois',
    title: 'PHASE 5 – Gouvernance continue (3 à 5 h / mois)',
    bullets: ['Rencontre mensuelle', 'Suivi des indicateurs', 'Recommandations stratégiques'],
    card:
      'rounded-xl border-2 border-emerald-400/80 border-l-[6px] border-l-emerald-400 bg-slate-950/92 p-2.5 shadow-lg ring-1 ring-emerald-500/25 backdrop-blur-sm',
    panel:
      'rounded-2xl border-2 border-emerald-400/75 border-l-[8px] border-l-emerald-400 bg-slate-950/96 p-4 shadow-inner ring-1 ring-emerald-500/30',
  },
];

export default function ComitePRIsME() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isCommunicationModalOpen, setIsCommunicationModalOpen] = useState(false);
  const [isEbaucheCadreBZOpen, setIsEbaucheCadreBZOpen] = useState(false);
  const [isProcedureModalOpen, setIsProcedureModalOpen] = useState(false);
  const [isProcedureCloneCatalogueOpen, setIsProcedureCloneCatalogueOpen] = useState(false);
  const [isGouvernanceModalOpen, setIsGouvernanceModalOpen] = useState(false);
  const [isGouvernance20ModalOpen, setIsGouvernance20ModalOpen] = useState(false);
  const [isComiteTIPrismeOpen, setIsComiteTIPrismeOpen] = useState(false);
  const [isComiteTIClientsDashboardOpen, setIsComiteTIClientsDashboardOpen] = useState(false);
  const [isCharteTempsOpen, setIsCharteTempsOpen] = useState(false);
  const [isUserCreationGuideOpen, setIsUserCreationGuideOpen] = useState(false);
  const [dashboardTotals, setDashboardTotals] = useState<ComiteDashboardTotals | null>(null);
  const { dates: lexDates } = useLexLastSync();

  // Liste des emails autorisés à accéder au Comité PRIsME
  const emailsAutorisesPRIsME = [
    'dany.bourassa@bzinc.ca',
    'jean-pierre.leclerc@bzinc.ca',
    'eric.hains@bzinc.ca',
    'francis.marquis@bzinc.ca',
    'jean-michel.caouette@bzinc.ca',
    'pierre-luc.fortin@bzinc.ca',
    'stephane.paradis@bzinc.ca',
    'eric.durand@bzinc.ca',
  ];

  // Vérifier l'accès au chargement
  useEffect(() => {
    if (!user?.email || !emailsAutorisesPRIsME.includes(user.email.toLowerCase())) {
      navigate('/comites');
    }
  }, [user, navigate]);

  // Charger les totaux du dashboard Comité TI (clients) pour la tuile
  useEffect(() => {
    (async () => {
      try {
        const d = await getComiteDashboard();
        setDashboardTotals(d.totals);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  // Ne pas afficher le contenu si l'utilisateur n'est pas autorisé
  if (!user?.email || !emailsAutorisesPRIsME.includes(user.email.toLowerCase())) {
    return null;
  }

  const leaders = [
    {
      name: "Jean-Pierre Leclerc",
      title: "Directeur Service TI",
      avatar: "/JP.png",
      vision: "Jean-Pierre dirige le Comité PRIsME avec une vision claire : faire de PRIsME l'outil d'excellence opérationnelle pour tous les Primes de BZ. Il voit PRIsME comme le différenciateur qui positionne BZ comme leader en excellence TI.",
      attentes: "Jean-Pierre s'attend à ce que le Comité PRIsME garantisse la qualité, la cohérence et l'évolution continue de PRIsME. Il veut voir chaque Prime utiliser PRIsME pour livrer de l'excellence constante et personnalisée à leurs clients.",
      pagePRIsME: "La page Comité PRIsME reflète notre gouvernance produit : communication interne, excellence opérationnelle, évolution stratégique. C'est notre espace de pilotage PRIsME."
    },
    {
      name: "Eric Hains",
      title: "DA Service TI & Chargé de projet",
      avatar: "/EH.png",
      vision: "Eric apporte au Comité PRIsME son expertise opérationnelle et sa vision volume. Il transforme PRIsME en outil d'exécution efficace pour les Primes. Il voit PRIsME comme l'accélérateur de productivité et de qualité.",
      attentes: "Eric s'attend à ce que PRIsME simplifie la vie des Primes tout en garantissant l'excellence. Il veut voir PRIsME utilisé par tous les Primes pour réduire les délais et améliorer la satisfaction client.",
      pagePRIsME: "La page Comité PRIsME représente pour Eric l'opportunité de piloter l'évolution opérationnelle de PRIsME : simplicité, efficacité, qualité. C'est l'outil qui transforme vision en exécution."
    },
    {
      name: "Francis Marquis",
      title: "Directeur Cybersécurité & Solutions",
      avatar: "/FM.png",
      vision: "Francis apporte au Comité PRIsME son expertise cybersécurité et sa rigueur opérationnelle. Il garantit que PRIsME respecte les standards de sécurité et de conformité. Il voit PRIsME comme l'outil qui standardise l'excellence sécuritaire.",
      attentes: "Francis s'attend à ce que PRIsME intègre les meilleures pratiques de cybersécurité et de conformité. Il veut voir PRIsME utilisé pour livrer des documents sécuritaires et conformes aux normes.",
      pagePRIsME: "La page Comité PRIsME représente pour Francis l'opportunité de garantir la qualité sécuritaire de PRIsME : conformité, standards, excellence. C'est l'outil qui aligne sécurité et valeur client."
    },
    {
      name: "Jean-Michel Caouette",
      title: "DA Service TI",
      avatar: "/JMC.png",
      vision: "Jean-Michel apporte au Comité PRIsME son expertise client et sa vision relationnelle. Il transforme PRIsME en outil de personnalisation et de satisfaction client. Il voit PRIsME comme l'outil qui renforce la relation client.",
      attentes: "Jean-Michel s'attend à ce que PRIsME permette aux Primes de personnaliser chaque relation client tout en respectant les standards. Il veut voir PRIsME utilisé pour créer des expériences client exceptionnelles.",
      pagePRIsME: "La page Comité PRIsME représente pour Jean-Michel l'opportunité de piloter la valeur client de PRIsME : personnalisation, satisfaction, excellence. C'est l'outil qui transforme relation en valeur."
    },
    {
      name: "Dany Bourassa",
      title: "Fondateur PRIsME & Président",
      avatar: "/DB.png",
      vision: "Dany est le fondateur de PRIsME et apporte au Comité PRIsME sa vision stratégique globale et sa passion pour l'excellence. Il voit PRIsME comme le différenciateur qui positionne BZ comme leader en excellence TI et qui transforme notre façon de livrer de la valeur.",
      attentes: "Dany s'attend à ce que PRIsME soit utilisé par tous les Primes pour livrer de l'excellence constante et personnalisée. Il veut voir PRIsME devenir l'ADN de BZ, l'outil qui transforme notre expertise en avantage concurrentiel durable.",
      pagePRIsME: "La page Comité PRIsME représente pour Dany l'opportunité de piloter l'évolution stratégique de PRIsME : gouvernance, communication, excellence. C'est l'espace où la vision devient réalité."
    }
  ];

  const totalPoints = communicationPRIsMEPrimes.ordreDuJour.reduce((sum, sujet) => sum + sujet.points.length, 0);
  const totalDuree = communicationPRIsMEPrimes.ordreDuJour.reduce((sum, sujet) => sum + sujet.dureeMinutes, 0);

  // Données des tuiles du Comité PRIsME au format standard
  const comitePRIsMETiles: AikupItem[] = [
    {
      id: 1,
      title: 'Comité TI PRIsME',
      category: 'Comité TI',
      description: 'Gestion complète des réunions: participants, points, notes, actions, résumé et validations (checklist).',
      aiSuggestion: 'Outil de gouvernance pour le Comité TI PRIsME',
      kpiScore: 95,
      imageUrl: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=1200&auto=format&fit=crop&q=80',
      inKnowledgeBase: true,
    },
    {
      id: 2,
      title: 'Comité TI - Clients',
      category: 'Dashboard',
      description: `Vue consolidée des comités TI des clients (en cours: ${dashboardTotals?.en_cours ?? 0}, complétées: ${dashboardTotals?.completees ?? 0}, validées: ${dashboardTotals?.valides ?? 0}, à valider: ${dashboardTotals?.a_valider ?? 0}) avec recherche.`,
      aiSuggestion: 'Dashboard de suivi des comités TI clients',
      kpiScore: 90,
      imageUrl: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&auto=format&fit=crop&q=80',
      inKnowledgeBase: true,
    },
    {
      id: 3,
      title: 'Créer un utilisateur PRISME',
      category: 'Procédure',
      description: 'Comment créer des accès pour PRISME MASTER, PRISME EMPLOYÉ et PRISME CLIENT (avec rappels: Administration PRISME BZ, TactiCE RH, et droits hardcodés).',
      aiSuggestion: 'Guide de création d\'utilisateurs PRIsME',
      kpiScore: 85,
      imageUrl: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=1200&q=80',
      inKnowledgeBase: true,
      lastSyncDate: lexDates['creer_utilisateur_prisme_last_ai_analysis'],
    },
    {
      id: 4,
      title: communicationPRIsMEPrimes.titre,
      category: 'Communication',
      description: communicationPRIsMEPrimes.objectifPrincipal,
      aiSuggestion: 'Communication officielle du Comité PRIsME',
      kpiScore: 88,
      imageUrl: 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1200&auto=format&fit=crop&q=80',
      inKnowledgeBase: false,
    },
    {
      id: 5,
      title: 'Procédure Clone PRIsME Client',
      category: 'Procédure',
      description: 'Guide étape par étape pour créer un nouveau PRIsME client à partir d\'un client existant. Inclut la configuration Git, Railway, la personnalisation et les tests finaux.',
      aiSuggestion: 'Procédure de clonage PRIsME client',
      kpiScore: 92,
      imageUrl: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=1200&auto=format&fit=crop&q=80',
      inKnowledgeBase: true,
      lastSyncDate: lexDates['procedure_clone_prisme_client_last_ai_analysis'],
    },
    {
      id: 6,
      title: 'Clone Catalogue des Actifs',
      category: 'Procédure',
      description: 'Cloner le Catalogue des actifs d\'un client à un autre. Sources de référence : PRIsME Ville de Saint-Lin ou POB.',
      aiSuggestion: 'Procédure de clonage catalogue actifs',
      kpiScore: 87,
      imageUrl: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&auto=format&fit=crop&q=80',
      inKnowledgeBase: false,
    },
    {
      id: 7,
      title: 'Gouvernance PRIsME',
      category: 'Gouvernance',
      description: 'Le cadre PRIsME complet : 10 axes de maturité, gouvernance, roadmap 2026, RACI et rituels. Un outil qui coache l\'équipe et structure la transformation organisationnelle.',
      aiSuggestion: 'Cadre de gouvernance PRIsME',
      kpiScore: 95,
      imageUrl: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1200&auto=format&fit=crop&q=80',
      inKnowledgeBase: true,
      lastSyncDate: lexDates['gouvernance_prisme_last_ai_analysis'],
    },
    {
      id: 8,
      title: 'Gouvernance PRIsME 2.0',
      category: 'Gouvernance',
      description: 'Cadre structuré de la Gouvernance PRIsME 2.0 avec objectifs macro, axes/piliers et tableau des tâches détaillé pour 2026.',
      aiSuggestion: 'Formalisation complète de la gouvernance PRIsME pour 2026 avec plan d\'action détaillé',
      kpiScore: 96,
      imageUrl: 'https://images.unsplash.com/photo-1507925921958-8a62f3d1a50d?w=1200&auto=format&fit=crop&q=80',
      inKnowledgeBase: true,
      lastSyncDate: lexDates['gouvernance_prisme_2_0_last_ai_analysis'],
    },
    {
      ...carteEbaucheCadreBZ,
      id: 9,
    },
  ];

  // Handlers pour chaque tuile
  const handleTileClick = (tileId: number) => {
    switch (tileId) {
      case 1:
        setIsComiteTIPrismeOpen(true);
        break;
      case 2:
        setIsComiteTIClientsDashboardOpen(true);
        break;
      case 3:
        setIsUserCreationGuideOpen(true);
        break;
      case 4:
        setIsCommunicationModalOpen(true);
        break;
      case 5:
        setIsProcedureModalOpen(true);
        break;
      case 6:
        setIsProcedureCloneCatalogueOpen(true);
        break;
      case 7:
        setIsGouvernanceModalOpen(true);
        break;
      case 8:
        // Gouvernance PRIsME 2.0
        setIsGouvernance20ModalOpen(true);
        break;
      case 9:
        setIsEbaucheCadreBZOpen(true);
        break;
      default:
        break;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Header 
        title="Comité PRIsME" 
        subtitle="Garde rapprochée PRIsME : gouvernance produit, communication interne, excellence opérationnelle et déploiement stratégique."
      />

      <div className="max-w-7xl mx-auto px-8 pb-12">
        {/* Leaders Section */}
        <LeadersSection leaders={leaders} title="Membres du Comité PRIsME" />

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-md p-6 border-l-4 border-violet-500"
          >
            <div className="flex items-center gap-3 mb-2">
              <Presentation className="w-5 h-5 text-violet-600" />
              <p className="text-sm text-gray-600">Communications</p>
            </div>
            <p className="text-3xl font-bold text-gray-900">1</p>
            <p className="text-xs text-gray-500 mt-1">Communication officielle</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl shadow-md p-6 border-l-4 border-purple-500"
          >
            <div className="flex items-center gap-3 mb-2">
              <Target className="w-5 h-5 text-purple-600" />
              <p className="text-sm text-gray-600">Points de Présentation</p>
            </div>
            <p className="text-3xl font-bold text-gray-900">{totalPoints}</p>
            <p className="text-xs text-gray-500 mt-1">Points structurés</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-md p-6 border-l-4 border-indigo-500"
          >
            <div className="flex items-center gap-3 mb-2">
              <Clock className="w-5 h-5 text-indigo-600" />
              <p className="text-sm text-gray-600">Durée Totale</p>
            </div>
            <p className="text-3xl font-bold text-gray-900">{totalDuree}</p>
            <p className="text-xs text-gray-500 mt-1">Minutes</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl shadow-md p-6 border-l-4 border-pink-500"
          >
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-5 h-5 text-pink-600" />
              <p className="text-sm text-gray-600">Présentateurs</p>
            </div>
            <p className="text-3xl font-bold text-gray-900">{participantsComitePRIsME.length}</p>
            <p className="text-xs text-gray-500 mt-1">Membres comité</p>
          </motion.div>
        </div>

        {/* Grille de tuiles au format standard */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {comitePRIsMETiles.map((tile, index) => {
            // Afficher tous les membres du Comité PRIsME sur chaque tuile
            const customLeaders = leaders.map(leader => ({
              name: leader.name,
              avatar: leader.avatar,
              title: leader.title,
            }));

            return (
              <AikupCard
                key={tile.id}
                item={tile}
                onClick={() => handleTileClick(tile.id)}
                onExpertClick={() => {}}
                index={index}
                customLeaders={customLeaders}
              />
            );
          })}
        </div>

        {/* Charte de temps - Direction TI (dans le Comité PRIsME) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          whileHover={{ scale: 1.01, y: -3 }}
          className="relative overflow-hidden rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer group mt-6"
          onClick={() => setIsCharteTempsOpen((v) => !v)}
        >
          {/* Image de fond */}
          <div
            className="absolute inset-0 z-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
            style={{
              backgroundImage: 'url(https://images.unsplash.com/photo-1553877522-43269d4ea984?w=1200&auto=format&fit=crop&q=80)',
            }}
          />

          {/* Overlay : dégradé couleur PRIsME (lisible + identité visuelle) */}
          <div className="absolute inset-0 z-[1] bg-gradient-to-br from-indigo-950/90 via-violet-950/82 to-slate-950/88 transition-all duration-300 group-hover:from-indigo-900/92 group-hover:via-fuchsia-900/86 group-hover:to-slate-950/90" />

          {/* Contenu */}
          <div className="relative z-[2] p-6 text-white">
            {/* Header */}
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-2">
                <FileText className="w-6 h-6 text-amber-300 drop-shadow-sm shrink-0" />
                <span className="px-3 py-1 backdrop-blur-sm bg-gradient-to-r from-indigo-500/55 to-violet-500/50 border border-indigo-300/60 rounded-full text-xs font-semibold text-white shadow-sm">
                  Charte
                </span>
              </div>
              <button
                type="button"
                aria-expanded={isCharteTempsOpen}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsCharteTempsOpen((v) => !v);
                }}
                className="inline-flex items-center gap-2 rounded-full bg-indigo-500/35 px-3 py-1.5 text-xs font-semibold text-white border border-indigo-300/50 hover:bg-violet-500/40 hover:border-violet-300/60 transition-colors shadow-sm"
              >
                <span>{isCharteTempsOpen ? 'Réduire' : 'Détails'}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${isCharteTempsOpen ? 'rotate-180' : ''}`} />
              </button>
            </div>

            <h3 className="text-2xl font-bold mb-2 text-white drop-shadow-md tracking-tight">
              Charte de temps – Direction TI
            </h3>
            <p className="text-white/90 text-sm leading-relaxed max-w-3xl drop-shadow-sm">
              Charte officielle de gestion du temps utilisée par BZ Inc. (Direction TI). Structure standard en 5 phases appliquée aux mandats PRIsME.
            </p>

            {/* Résumé des phases — une teinte par phase */}
            <div className="mt-5 grid grid-cols-1 md:grid-cols-5 gap-2 text-xs">
              {CHARTE_TEMPS_PHASES.map((row) => (
                <div key={row.phase} className={row.card}>
                  <p className="mb-1 font-bold text-white drop-shadow-sm">{row.phase}</p>
                  <p className="text-white/90 font-medium leading-snug">{row.label}</p>
                  <p className="mt-1.5 font-bold text-amber-200 tabular-nums">{row.hours}</p>
                </div>
              ))}
            </div>

            {/* Détail des phases (affiché dans la tuile) */}
            {isCharteTempsOpen && (
              <div className="mt-5 space-y-4">
                {CHARTE_TEMPS_PHASES.map((row) => (
                  <div key={row.title} className={row.panel}>
                    <p className="text-sm font-bold text-white tracking-tight">{row.title}</p>
                    <ul className="mt-2 space-y-1.5 text-sm text-white/90 leading-relaxed">
                      {row.bullets.map((b) => (
                        <li key={b}>• {b}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Modals */}
      <ComiteTIPrismeModal
        isOpen={isComiteTIPrismeOpen}
        onClose={() => setIsComiteTIPrismeOpen(false)}
        title="🤝 Comité TI - PRIsME"
        scope="PRISME"
      />
      <ComiteTIClientsDashboardModal
        isOpen={isComiteTIClientsDashboardOpen}
        onClose={() => setIsComiteTIClientsDashboardOpen(false)}
      />
      <CommunicationPRIsMEPrimesModal
        isOpen={isCommunicationModalOpen}
        onClose={() => setIsCommunicationModalOpen(false)}
        communication={communicationPRIsMEPrimes}
      />
      <EbaucheCadreBZModal
        isOpen={isEbaucheCadreBZOpen}
        onClose={() => setIsEbaucheCadreBZOpen(false)}
      />
      <ProcedureClonePRIsMEModal
        isOpen={isProcedureModalOpen}
        onClose={() => setIsProcedureModalOpen(false)}
      />
      <ProcedureCloneCatalogueActifsModal
        isOpen={isProcedureCloneCatalogueOpen}
        onClose={() => setIsProcedureCloneCatalogueOpen(false)}
      />
      <GouvernancePRIsMEModal
        isOpen={isGouvernanceModalOpen}
        onClose={() => setIsGouvernanceModalOpen(false)}
      />
      <GouvernancePRIsME20Modal
        isOpen={isGouvernance20ModalOpen}
        onClose={() => setIsGouvernance20ModalOpen(false)}
      />
      <PrismeUserCreationGuideModal
        isOpen={isUserCreationGuideOpen}
        onClose={() => setIsUserCreationGuideOpen(false)}
      />
    </div>
  );
}
